import { urlFor } from '@sydle/ui-utils';
import type { ItemPath } from '../models/item/ItemPath';
import { ServiceDeskRoutes } from './router-utils';
import { getPathIdentifiers } from './utils.utils';

export class URLHelper {
  /**
   * Set a param in URL. If 'storybook' is true, than 'param' is set in the topmost
   * window.
   * @param param (string) param identifier to be used.
   * @param value (string) param value.
   * @param storybook (boolean) Specify if should set in topmost window or not.
   */
  static setURLParam(param: string, value: string, storybook = false): void {
    const windowRef = storybook ? window.top : window;
    if (windowRef?.history.replaceState) {
      const newURL = new URL(windowRef.location.href);
      newURL.searchParams.set(param, value);
      windowRef.history.replaceState(null, '', newURL.href);
    }
  }

  /**
   * Get a param from URL. If 'storybook' is true, than 'param' is fetched from the topmost
   * window.
   * @param param (string) param identifier in URL.
   * @param storybook (boolean) Specify if should get from topmost window or not.
   * @returns (string) param value.
   */
  static getURLParam(param: string, storybook = false): string | null {
    const windowRef = storybook ? window.top : window;
    const searchParams = new URLSearchParams(windowRef?.location.search);
    return searchParams.get(param);
  }

  /**
   * Get the hash from URL. If 'storybook' is true, the hash is fetched from the topmost
   * window.
   * @param storybook (boolean) Specify if should get from topmost window or not.
   * @returns (string) hash value.
   */
  static getURLHash(storybook = false): string | undefined {
    const windowRef = storybook ? window.top : window;
    return windowRef?.location.hash;
  }

  /**
   * Delete a param from URL. If 'storybook' is true, than 'param' is fetched from the topmost
   * window.
   * @param param (string) param identifier in URL.
   * @param storybook (boolean) Specify if should get from topmost window or not.
   */
  static deleteURLParam(param: string, storybook = false): void {
    const windowRef = storybook ? window.top : window;
    if (windowRef?.history.replaceState) {
      const newURL = new URL(windowRef.location.href);
      newURL.searchParams.delete(param);
      windowRef.history.replaceState(null, '', newURL.href);
    }
  }
}

export function getPathUrl(itemPath: ItemPath): string {
  if (itemPath.item) {
    const path =
      itemPath.item.friendlyUrl ??
      `${getPathIdentifiers(itemPath.item, itemPath.index)}+${itemPath.id ?? itemPath.item._id}`;
    return urlFor(ServiceDeskRoutes.PATH_WITH_ID, { pathWithId: path });
  }

  return urlFor(ServiceDeskRoutes.PATH_WITH_ID, { pathWithId: itemPath.id });
}
